import {
  Avatar,
  Box,
  createStyles,
  Flex,
  Image,
  Indicator,
  Navbar,
  rem,
  ScrollArea,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconBoxSeam,
  IconBuildingStore,
  IconBuildingWarehouse,
  IconCalendar,
  IconCash,
  IconCoinRupee,
  IconCreditCard,
  IconDatabase,
  IconFileFilled,
  IconFileInvoice,
  IconGauge,
  IconHomeDollar,
  IconMessageChatbot,
  IconNotebook,
  IconPill,
  IconProps,
  IconReceipt,
  IconReceiptRupee,
  IconReportAnalytics,
  IconScale,
  IconShoppingCartFilled,
  IconTag,
  IconUserCheck,
  IconUsers,
  IconUsersGroup,
} from "@tabler/icons-react";
import { observer } from "mobx-react-lite";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RootStoreContext } from "stores/RootStore";

import expiryStocks from "../../assets/icons/expiry-stocks.svg";
import hospitals from "../../assets/icons/hospitals-icon.svg";
import instoreSales from "../../assets/icons/instore-sales.svg";
import medicoInvoice from "../../assets/icons/medico-invoice.svg";
import offlinePickups from "../../assets/icons/offline-pickups.svg";
import onlineOrders from "../../assets/icons/online-orders.svg";
import onlinePickups from "../../assets/icons/online-pickups.svg";
import orderPreview from "../../assets/icons/orders-verview.svg";
import purchaseInvoices from "../../assets/icons/purchase-invoices.svg";
import purchaseReturns from "../../assets/icons/purchase-returns.svg";
import retailers from "../../assets/icons/retailers-icon.svg";
import salesReturns from "../../assets/icons/sales-returns.svg";
import stockists from "../../assets/icons/stockists-icon.svg";
import stocksList from "../../assets/icons/stocks-list.svg";
import medicologo from "../../assets/images/medicologo.png";

const useStyles = createStyles((theme) => ({
  navbar: {
    backgroundColor: theme.white,
    borderRight: `1px solid ${theme.colors.gray[3]}`,
    transition: "width 0.3s ease",
    height: "100vh",
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 1000,
  },

  header: {
    marginLeft: -theme.spacing.md,
    marginRight: -theme.spacing.md,
    color: theme.white,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    minHeight: 30,
  },

  links: {
    marginLeft: `calc(${theme.spacing.md} * -0.6)`,
    marginRight: `calc(${theme.spacing.md} * -1)`,
  },

  linksInner: {
    paddingBottom: theme.spacing.md,
  },

  control: {
    fontWeight: 600,
    display: "flex",
    width: "auto",
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.black,
    fontSize: theme.fontSizes.md,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    transition: "all 0.2s ease",

    "&:hover": {
      backgroundColor: "transparent",
      color: theme.colors.blue[7],
    },
  },

  controlActive: {
    backgroundColor: "transparent",
    color: theme.colors.blue[7],
    borderLeft: `3px solid ${theme.colors.blue[6]}`,
    paddingLeft: `calc(${theme.spacing.sm} - 3px)`,
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",

    "&:hover": {
      backgroundColor: "transparent",
      color: theme.colors.blue[8],
    },
  },

  link: {
    fontWeight: 600,
    display: "inline-block",
    textDecoration: "none",
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    margin: `0 ${theme.spacing.xs}`,
    fontSize: theme.fontSizes.md,
    color: theme.colors.blue[7],
    cursor: "pointer",
    transition: "all 0.2s ease",
    borderRadius: theme.radius.sm,

    "&:hover": {
      backgroundColor: "transparent",
      color: theme.colors.blue[7],
    },
  },

  footer: {
    marginLeft: -theme.spacing.md,
    marginRight: -theme.spacing.md,
    borderTop: `${rem(1)} solid ${theme.colors.gray[3]}`,
  },

  customThemeIcon: {
    "& > div": {
      width: "14.4px !important",
      height: "14.4px !important",
      minWidth: "14.4px !important",
      minHeight: "14.4px !important",
      maxWidth: "14.4px !important",
      maxHeight: "14.4px !important",
    },
    "&:hover": {
      "& svg": {
        color: "#1971c2 !important",
      },
    },
  },

  customSubmenuThemeIcon: {
    "& > div": {
      width: "16px !important",
      height: "16px !important",
      minWidth: "12px !important",
      minHeight: "12px !important",
      maxWidth: "30px !important",
      maxHeight: "30px !important",
    },
  },

  scrollArea: {
    "& .mantine-ScrollArea-scrollbar": {
      width: "4px !important",
      minWidth: "4px !important",
      maxWidth: "4px !important",
    },
    "& .mantine-ScrollArea-thumb": {
      width: "4px !important",
      minWidth: "4px !important",
      maxWidth: "4px !important",
    },
    "& .mantine-ScrollArea-scrollbar[data-orientation='vertical']": {
      width: "4px !important",
    },
    "& .mantine-ScrollArea-thumb[data-orientation='vertical']": {
      width: "4px !important",
    },
  },
}));

interface NavigationItem {
  label: string;
  icon: React.FC<IconProps>;
  to?: string;
  links?: {
    label: string;
    link: string;
    subIcon: string | React.FC<IconProps>;
    isHeading?: boolean;
  }[];
}

const NavbarMenu: React.FC = () => {
  const { t } = useTranslation(["navbar", "labels"]);
  const { classes, cx } = useStyles();

  const navigate = useNavigate();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const labelsContainerRef = useRef<HTMLDivElement>(null);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [menuWidthPx, setMenuWidthPx] = useState<number>(64);

  const { navMenuStore } = useContext(RootStoreContext);

  // const developmentOnly = process.env.REACT_APP_ENVIRONMENT === "dev";

  const navigation: NavigationItem[] = [
    {
      label: t("dashboard", { ns: "labels" }),
      icon: IconGauge,
      to: "/dashboard?tab=online-pickups",
    },
    {
      label: t("orders", { ns: "labels" }),
      icon: IconBoxSeam,
      links: [
        {
          label: t("totalOrdersOverview"),
          link: "/orders/orders-overview",
          subIcon: orderPreview,
        },
        {
          label: t("onlinePickups"),
          link: "/orders/online-pickups",
          subIcon: onlinePickups,
        },
        {
          label: t("offlinePickups"),
          link: "/orders/offline-pickups",
          subIcon: offlinePickups,
        },
        {
          label: t("onlineOrders"),
          link: "/orders/online-orders",
          subIcon: onlineOrders,
        },
        {
          label: t("Customers"),
          link: "/orders/b2b-customers",
          subIcon: retailers,
        },
      ],
    },
    {
      label: "Customers",
      icon: IconUsers,
      to: "/orders/b2b-customers",
    },
    {
      label: "MedicoStores AI",
      icon: IconMessageChatbot,
      to: "/ai-support",
    },
    {
      label: "Payments",
      icon: IconCoinRupee,
      to: "/payments/list",
    },
    {
      label: "Collections",
      icon: IconReceiptRupee,
      to: "/payments/collections",
    },
  ];

  // MEDICO DETAILS section for all customer types
  navigation.push({
    label: "Medico Details",
    icon: IconBuildingStore,
    links: [
      {
        label: "BILLING DETAILS",
        link: "",
        subIcon: IconReceipt,
        isHeading: true,
      },
      {
        label: t("medicoInvoice"),
        link: "/billing/invoices",
        subIcon: medicoInvoice,
      },
      {
        label: "STORES",
        link: "",
        subIcon: IconBuildingStore,
        isHeading: true,
      },
      {
        label: t("Stores", { ns: "labels" }),
        link: "/stores/dashboard",
        subIcon: IconBuildingStore,
      },
    ],
  });

  navigation.push({
    label: t("Purchases", { ns: "labels" }),
    icon: IconCash,
    links: [
      {
        label: t("Purchase Invoice", { ns: "labels" }),
        link: "/purchases/invoices",
        subIcon: purchaseInvoices,
      },
      {
        label: t("Purchase Orders", { ns: "labels" }),
        link: "/purchases/orders",
        subIcon: orderPreview,
      },
      {
        label: t("Purchase Returns", { ns: "labels" }),
        link: "/purchases/returns",
        subIcon: purchaseReturns,
      },
      {
        label: "REPORTS",
        link: "",
        subIcon: IconFileFilled,
        isHeading: true,
      },
      {
        label: "Purchase Registry",
        link: "/reports/purchase/purchase-registry",
        subIcon: IconFileInvoice,
      },
      {
        label: "Party Wise",
        link: "/reports/purchase/party-wise",
        subIcon: IconUsers,
      },
      {
        label: "Item Wise",
        link: "/reports/purchase/item-wise",
        subIcon: IconPill,
      },
      {
        label: "Batch Wise",
        link: "/reports/purchase/batch-wise",
        subIcon: IconShoppingCartFilled,
      },
      {
        label: "MFR Wise",
        link: "/reports/purchase/mfr-wise",
        subIcon: IconFileFilled,
      },
    ],
  });
  navigation.push({
    label: t("Sales", { ns: "labels" }),
    icon: IconReceipt,
    links: [
      {
        label: t("Sales", { ns: "labels" }),
        link: "/sales/instore",
        subIcon: instoreSales,
      },
      // {
      //   label: t("Online Sales", { ns: "labels" }),
      //   link: "/sales/online",
      //   subIcon: onlineSales,
      // },
      {
        label: t("Sales Returns", { ns: "labels" }),
        link: "/sales/returns",
        subIcon: salesReturns,
      },
      {
        label: "REPORTS",
        link: "",
        subIcon: IconFileFilled,
        isHeading: true,
      },
      {
        label: "Sales Registry",
        link: "/reports/sales/sales-registry",
        subIcon: IconFileInvoice,
      },
      {
        label: "Day Wise",
        link: "/reports/sales/day-wise",
        subIcon: IconCalendar,
      },
      {
        label: "Customers",
        link: "/reports/sales/customers",
        subIcon: IconUsers,
      },
      {
        label: "Item Wise",
        link: "/reports/sales/item-wise",
        subIcon: IconPill,
      },
      {
        label: "Batch Wise",
        link: "/reports/sales/batch-wise",
        subIcon: IconShoppingCartFilled,
      },
    ],
  });
  navigation.push({
    label: "Offers",
    icon: IconTag,
    to: "/offers",
  });
  navigation.push({
    label: t("Journals", { ns: "labels" }),
    icon: IconNotebook,
    links: [
      {
        label: t("Journals", { ns: "labels" }),
        link: "/journals/dashboard",
        subIcon: IconCash,
      },
      {
        label: "REPORTS",
        link: "",
        subIcon: IconFileFilled,
        isHeading: true,
      },
      {
        label: "Day Book",
        link: "/reports/journals/day-book",
        subIcon: IconCalendar,
      },
      {
        label: "Cash Book",
        link: "/reports/journals/cash-book",
        subIcon: IconCash,
      },
      {
        label: "Trail Balance",
        link: "/reports/journals/trail-balance",
        subIcon: IconScale,
      },
      {
        label: "Creditors",
        link: "/reports/journals/creditors",
        subIcon: IconUsers,
      },
      {
        label: "Debitors",
        link: "/reports/journals/debitors",
        subIcon: IconUserCheck,
      },
    ],
  });
  navigation.push({
    label: t("Stocks", { ns: "labels" }),
    icon: IconBoxSeam,
    links: [
      {
        label: t("Stock", { ns: "labels" }),
        link: "/stocks/list",
        subIcon: stocksList,
      },

      {
        label: t("Expiry Management", { ns: "labels" }),
        link: "/stocks/expiry-list",
        subIcon: expiryStocks,
      },

      {
        label: t("Stock Summary", { ns: "labels" }),
        link: "/stocks/summary",
        subIcon: IconReportAnalytics,
      },
      {
        label: t("Stock Fast Slow Moving", { ns: "labels" }),
        link: "/stocks/fast-slow-moving",
        subIcon: IconGauge,
      },
      {
        label: t("Product List", { ns: "labels" }),
        link: "/stocks/products-list",
        subIcon: IconPill,
      },
    ],
  });

  // Add INVENTORY and CUSTOMERS sections to MEDICO DETAILS

  const medicoDetailsIndex = navigation.findIndex(
    (item) => item.label === "Medico Details"
  );
  if (medicoDetailsIndex !== -1) {
    navigation[medicoDetailsIndex].links?.push(
      {
        label: "INVENTORY",
        link: "",
        subIcon: IconBuildingWarehouse,
        isHeading: true,
      },
      {
        label: t("uploadInventory"),
        link: "/inventory/upload",
        subIcon: stocksList,
      },
      {
        label: "CUSTOMERS",
        link: "",
        subIcon: IconUsersGroup,
        isHeading: true,
      },
      {
        label: t("stockists", { ns: "labels" }),
        link: "/customers/stockists",
        subIcon: stockists,
      },
      {
        label: t("hospitals", { ns: "labels" }),
        link: "/customers/hospitals",
        subIcon: hospitals,
      },
      {
        label: t("retailers", { ns: "labels" }),
        link: "/customers/retailers",
        subIcon: retailers,
      }
    );
  }

  // Add ACCOUNTING section for STOCKIST
  navigation.push({
    label: "Accounting",
    icon: IconDatabase,
    links: [
      {
        label: "MASTER DATA",
        link: "",
        subIcon: IconDatabase,
        isHeading: true,
      },
      {
        label: t("Warehouses", { ns: "labels" }),
        link: "/warehouses",
        subIcon: IconBuildingWarehouse,
      },
      {
        label: t("Bank Accounts", { ns: "labels" }),
        link: "/bank-accounts",
        subIcon: IconCreditCard,
      },
      {
        label: t("Heads", { ns: "labels" }),
        link: "/heads/dashboard",
        subIcon: IconReceipt,
      },
      {
        label: t("Employees", { ns: "labels" }),
        link: "/employees",
        subIcon: IconUsers,
      },
      {
        label: "Accounts Prefix",
        link: "/accounts-prefix",
        subIcon: IconTag,
      },
      {
        label: "LEDGERS",
        link: "",
        subIcon: IconReceipt,
        isHeading: true,
      },
      {
        label: t("Ledgers", { ns: "labels" }),
        link: "/ledgers",
        subIcon: IconReceipt,
      },
      {
        label: "PROFIT & LOSS",
        link: "",
        subIcon: IconHomeDollar,
        isHeading: true,
      },
      {
        label: t("Profit & Loss", { ns: "labels" }),
        link: "/profit&loss",
        subIcon: IconHomeDollar,
      },
    ],
  });

  // GST section for all customer types
  navigation.push({
    label: "GST",
    icon: IconFileInvoice,
    links: [
      {
        label: "GSTR-2",
        link: "",
        subIcon: IconFileInvoice,
        isHeading: true,
      },
      {
        label: "Inward Suppliers",
        link: "/gst/inward-suppliers",
        subIcon: IconUsers,
      },
      {
        label: "GSTR-3B",
        link: "",
        subIcon: IconFileInvoice,
        isHeading: true,
      },
      {
        label: "Outward Suppliers",
        link: "/gst/outward-suppliers",
        subIcon: IconUsers,
      },
      // {
      //   label: "Company GST",
      //   link: "/gst/company",
      //   subIcon: IconBuildingStore,
      // },
    ],
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        navMenuStore.setIsNavMenuOpen(false);
        setExpandedItem(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [navMenuStore]);

  useEffect(() => {
    if (navMenuStore.isNavMenuOpen) {
      requestAnimationFrame(() => {
        const container = labelsContainerRef.current;
        if (!container) return;
        const labels = Array.from(
          container.querySelectorAll<HTMLSpanElement>("span.__main-label__")
        );
        // If there are no labels (icon-only mode), keep compact width
        if (labels.length === 0) {
          setMenuWidthPx(64);
          return;
        }
        const max = labels.reduce((m, el) => Math.max(m, el.offsetWidth), 0);

        const computed = Math.max(110, Math.min(240, max + 28));
        setMenuWidthPx(computed);
      });
    } else {
      setMenuWidthPx(64);
    }
  }, [navMenuStore.isNavMenuOpen, navigation, t]);

  const handleNavigation = (to?: string, link?: string) => {
    if (to) {
      navigate(to);

      navMenuStore.setIsNavMenuOpen(false);
      setExpandedItem(null);
    } else if (link) {
      navigate(link);

      navMenuStore.setIsNavMenuOpen(false);
      setExpandedItem(null);
    }
  };

  const handleMainItemClick = (item: NavigationItem) => {
    // Force close any existing submenu first
    setExpandedItem(null);

    // Use setTimeout to ensure state is reset before setting new state
    setTimeout(() => {
      if (item.to) {
        // If item has direct navigation, navigate directly
        handleNavigation(item.to);
      } else if (item.links && item.links.length > 0) {
        // If item has submenu, open it
        setExpandedItem(item.label);
      }
    }, 0);
  };

  return (
    <div ref={sidebarRef}>
      <Navbar
        height={800}
        style={{
          width: navMenuStore.isNavMenuOpen ? `${menuWidthPx}px` : "64px",
          transition: "width 0.3s ease",
        }}
        p="sm"
        className={classes.navbar}
      >
        <Navbar.Section
          className={classes.header}
          style={{
            borderBottom: "none",
            minHeight: navMenuStore.isNavMenuOpen ? 0 : undefined,
            paddingTop: navMenuStore.isNavMenuOpen ? 0 : undefined,
            marginTop: navMenuStore.isNavMenuOpen ? 0 : undefined,
          }}
        >
          {navMenuStore.isNavMenuOpen ? null : (
            <Flex justify="center" align="center">
              <a href="/dashboard" style={{ textDecoration: "none" }}>
                <Image
                  src={medicologo}
                  alt="Medico Logo"
                  width={32}
                  height={32}
                  style={{ cursor: "pointer" }}
                />
              </a>
            </Flex>
          )}
        </Navbar.Section>

        <Navbar.Section
          grow
          className={classes.links}
          component={ScrollArea}
          classNames={{ scrollbar: classes.scrollArea }}
        >
          <div className={classes.linksInner} ref={labelsContainerRef}>
            {navigation.map((item) => {
              const isExpanded = expandedItem === item.label;
              const isActive = false;
              const isRedItem = item.label === "Offers";
              const activeColor = isRedItem ? "#e03131" : "#1971c2";
              const inactiveColor = isRedItem
                ? "#e03131"
                : "rgba(0, 0, 0, 0.54)";
              const collapsedInactiveColor = isRedItem ? "#e03131" : "#6c757d";

              return (
                <Box key={item.label} style={{ position: "relative" }}>
                  {navMenuStore.isNavMenuOpen ? (
                    <UnstyledButton
                      onClick={() => handleMainItemClick(item)}
                      className={cx(classes.control, {
                        [classes.controlActive]: isActive || isExpanded,
                      })}
                      style={{
                        width: "100%",
                        backgroundColor: "transparent",
                        borderLeft:
                          navMenuStore.isNavMenuOpen && (isActive || isExpanded)
                            ? `3px solid ${activeColor}`
                            : undefined,
                        paddingLeft:
                          navMenuStore.isNavMenuOpen && (isActive || isExpanded)
                            ? "calc(12px - 3px)"
                            : "12px",
                        paddingTop: navMenuStore.isNavMenuOpen ? 6 : undefined,
                        paddingBottom: navMenuStore.isNavMenuOpen
                          ? 6
                          : undefined,
                        marginBottom: navMenuStore.isNavMenuOpen
                          ? 0
                          : undefined,
                      }}
                    >
                      <Flex direction="column" align="center" gap={6}>
                        <Indicator
                          label="Ai"
                          size={16}
                          offset={12}
                          position="top-end"
                          disabled={item.label !== "MedicoStores AI"}
                          color="#00a86b"
                          styles={{
                            indicator: {
                              fontSize: "10px",
                              fontWeight: 700,
                              padding: "0 4px",
                            },
                          }}
                        >
                          <ThemeIcon
                            variant="transparent"
                            size={40}
                            className={classes.customThemeIcon}
                          >
                            <item.icon
                              size="1.8rem"
                              color={
                                item.label === "MedicoStores AI"
                                  ? "#00a86b"
                                  : navMenuStore.isNavMenuOpen &&
                                    (isActive || isExpanded)
                                  ? activeColor
                                  : inactiveColor
                              }
                            />
                          </ThemeIcon>
                        </Indicator>
                      </Flex>
                    </UnstyledButton>
                  ) : (
                    <Tooltip
                      label={item.label}
                      position="right"
                      withArrow
                      offset={12}
                      zIndex={4001}
                      withinPortal
                    >
                      <UnstyledButton
                        onClick={() => handleMainItemClick(item)}
                        className={cx(classes.control, {
                          [classes.controlActive]: isActive || isExpanded,
                        })}
                      >
                        <Flex align="center" justify="center" gap="xs">
                          <Indicator
                            label="Ai"
                            size={14}
                            offset={8}
                            position="top-end"
                            disabled={item.label !== "MedicoStores AI"}
                            color="#00a86b"
                            styles={{
                              indicator: {
                                fontSize: "8px",
                                fontWeight: 700,
                                padding: "0 2px",
                              },
                            }}
                          >
                            <ThemeIcon
                              variant="transparent"
                              size={36}
                              color={
                                item.label === "MedicoStores AI"
                                  ? "#00a86b"
                                  : isActive || isExpanded
                                  ? activeColor
                                  : collapsedInactiveColor
                              }
                              className={classes.customThemeIcon}
                            >
                              <item.icon
                                size="1.6rem"
                                color={
                                  item.label === "MedicoStores AI"
                                    ? "#00a86b"
                                    : isActive || isExpanded
                                    ? activeColor
                                    : collapsedInactiveColor
                                }
                              />
                            </ThemeIcon>
                          </Indicator>
                        </Flex>
                      </UnstyledButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </div>
        </Navbar.Section>

        {/* Right Sidebar for Submenu Items */}
        {expandedItem && (
          <div
            style={{
              position: "absolute",
              left: "100%",
              top: 0,
              width: "220px",
              height: "100%",
              backgroundColor: "white",
              borderLeft: "1px solid #e1e5e9",
              borderRight: "1px solid #e1e5e9",
              zIndex: 999,
              overflowY: "auto",
            }}
          >
            {/* Submenu Header with Close */}
            <div
              style={{
                padding: "8px 10px 6px 10px",
              }}
            >
              <Flex align="center" justify="flex-start" gap="xs">
                <Text
                  style={{
                    fontSize: "16px",
                    lineHeight: "28px",
                    fontWeight: 900,
                    color: "#1971c2",
                    textTransform: "uppercase",
                    marginLeft: "7px",
                    marginTop: "7px",
                  }}
                >
                  {expandedItem === t("Journals", { ns: "labels" })
                    ? "JOURNALS"
                    : expandedItem === "MEDICO DETAILS"
                    ? "MEDICO DETAILS"
                    : expandedItem === "ACCOUNTING"
                    ? "ACCOUNTING"
                    : expandedItem}
                </Text>
              </Flex>
            </div>

            {/* Submenu Items */}
            <div style={{ padding: "0px 8px" }}>
              {navigation
                .find((item) => item.label === expandedItem)
                ?.links?.map((link) =>
                  link.isHeading ? (
                    <div
                      key={link.label}
                      style={{
                        padding: "12px 4px 6px 4px",
                        marginBottom: "4px",
                        borderBottom:
                          link.label === "SALES REPORTS" ||
                          link.label === "JOURNALS REPORTS" ||
                          link.label === "REPORTS" ||
                          link.label === "BILLING DETAILS" ||
                          link.label === "STORES" ||
                          link.label === "INVENTORY" ||
                          link.label === "CUSTOMERS" ||
                          link.label === "MASTER DATA" ||
                          link.label === "LEDGERS" ||
                          link.label === "JOURNALS" ||
                          link.label === "PROFIT & LOSS" ||
                          link.label === "GSTR-2" ||
                          link.label === "GSTR-3B"
                            ? "none"
                            : "1px solid #e9ecef",
                      }}
                    >
                      <Text
                        style={{
                          fontSize:
                            link.label === "SALES REPORTS" ||
                            link.label === "JOURNALS REPORTS" ||
                            link.label === "REPORTS" ||
                            link.label === "BILLING DETAILS" ||
                            link.label === "STORES" ||
                            link.label === "INVENTORY" ||
                            link.label === "CUSTOMERS" ||
                            link.label === "MASTER DATA" ||
                            link.label === "LEDGERS" ||
                            link.label === "JOURNALS" ||
                            link.label === "PROFIT & LOSS" ||
                            link.label === "GSTR-2" ||
                            link.label === "GSTR-3B"
                              ? "16px"
                              : "12px",
                          lineHeight:
                            link.label === "SALES REPORTS" ||
                            link.label === "JOURNALS REPORTS" ||
                            link.label === "REPORTS" ||
                            link.label === "BILLING DETAILS" ||
                            link.label === "STORES" ||
                            link.label === "INVENTORY" ||
                            link.label === "CUSTOMERS" ||
                            link.label === "MASTER DATA" ||
                            link.label === "LEDGERS" ||
                            link.label === "JOURNALS" ||
                            link.label === "PROFIT & LOSS" ||
                            link.label === "GSTR-2" ||
                            link.label === "GSTR-3B"
                              ? "28px"
                              : "12px",
                          marginLeft:
                            link.label === "SALES REPORTS" ||
                            link.label === "JOURNALS REPORTS" ||
                            link.label === "REPORTS" ||
                            link.label === "BILLING DETAILS" ||
                            link.label === "STORES" ||
                            link.label === "INVENTORY" ||
                            link.label === "CUSTOMERS" ||
                            link.label === "MASTER DATA" ||
                            link.label === "LEDGERS" ||
                            link.label === "JOURNALS" ||
                            link.label === "PROFIT & LOSS" ||
                            link.label === "GSTR-2" ||
                            link.label === "GSTR-3B"
                              ? "5px"
                              : "12px",
                          fontWeight:
                            link.label === "SALES REPORTS" ||
                            link.label === "JOURNALS REPORTS" ||
                            link.label === "REPORTS" ||
                            link.label === "BILLING DETAILS" ||
                            link.label === "STORES" ||
                            link.label === "INVENTORY" ||
                            link.label === "CUSTOMERS" ||
                            link.label === "MASTER DATA" ||
                            link.label === "LEDGERS" ||
                            link.label === "JOURNALS" ||
                            link.label === "PROFIT & LOSS" ||
                            link.label === "GSTR-2" ||
                            link.label === "GSTR-3B"
                              ? 800
                              : 700,
                          color:
                            link.label === "SALES REPORTS" ||
                            link.label === "JOURNALS REPORTS" ||
                            link.label === "REPORTS" ||
                            link.label === "BILLING DETAILS" ||
                            link.label === "STORES" ||
                            link.label === "INVENTORY" ||
                            link.label === "CUSTOMERS" ||
                            link.label === "MASTER DATA" ||
                            link.label === "LEDGERS" ||
                            link.label === "JOURNALS" ||
                            link.label === "PROFIT & LOSS" ||
                            link.label === "GSTR-2" ||
                            link.label === "GSTR-3B"
                              ? "rgb(25, 113, 194)"
                              : "#1971c2",
                          textTransform: "uppercase",
                          letterSpacing:
                            link.label === "SALES REPORTS" ||
                            link.label === "JOURNALS REPORTS" ||
                            link.label === "REPORTS" ||
                            link.label === "BILLING DETAILS" ||
                            link.label === "STORES" ||
                            link.label === "INVENTORY" ||
                            link.label === "CUSTOMERS" ||
                            link.label === "MASTER DATA" ||
                            link.label === "LEDGERS" ||
                            link.label === "JOURNALS" ||
                            link.label === "PROFIT & LOSS" ||
                            link.label === "GSTR-2" ||
                            link.label === "GSTR-3B"
                              ? "1px"
                              : "0.5px",
                          textShadow:
                            link.label === "SALES REPORTS" ||
                            link.label === "JOURNALS REPORTS" ||
                            link.label === "REPORTS" ||
                            link.label === "BILLING DETAILS" ||
                            link.label === "STORES" ||
                            link.label === "INVENTORY" ||
                            link.label === "CUSTOMERS" ||
                            link.label === "MASTER DATA" ||
                            link.label === "LEDGERS" ||
                            link.label === "JOURNALS" ||
                            link.label === "PROFIT & LOSS" ||
                            link.label === "GSTR-2" ||
                            link.label === "GSTR-3B"
                              ? "0 1px 2px rgba(0,0,0,0.1)"
                              : "none",
                        }}
                      >
                        {link.label}
                      </Text>
                    </div>
                  ) : (
                    <Flex
                      key={link.label}
                      direction="row"
                      align="center"
                      justify="flex-start"
                      gap={12}
                      style={{
                        padding: "7px 4px",
                        marginBottom: "2px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        border: "1px solid transparent",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#e3f2fd";
                        e.currentTarget.style.borderColor = "#1971c2";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.borderColor = "transparent";
                      }}
                      onClick={() => handleNavigation(undefined, link.link)}
                    >
                      <ThemeIcon
                        variant="transparent"
                        size={20}
                        className={classes.customSubmenuThemeIcon}
                      >
                        {typeof link.subIcon === "string" ? (
                          <Avatar
                            src={link.subIcon}
                            alt="icon"
                            size="sm"
                            style={{
                              width:
                                link.link === "/payments/upi/accounts"
                                  ? 18
                                  : "",
                              height:
                                link.link === "/purchases/returns" ||
                                link.link === "/sales/returns"
                                  ? 14
                                  : "",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              filter: "brightness(0) saturate(100%)",
                              opacity: 0.87,
                            }}
                          />
                        ) : (
                          <link.subIcon
                            size={16}
                            style={{
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                          />
                        )}
                      </ThemeIcon>
                      <Text
                        style={{
                          fontSize: "14px",
                          color: "rgba(0, 0, 0, 0.87)",
                        }}
                      >
                        {link.label}
                      </Text>
                    </Flex>
                  )
                )}
            </div>
          </div>
        )}
      </Navbar>
    </div>
  );
};

export default observer(NavbarMenu);
